import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, effect, inject, OnInit, computed, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { ButtonDirective } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { concatMap, from, of, Subscription, timer } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ProjectsService } from '../../core/services/projects.service';
import { AuthService } from '../../core/services/auth.service';
import { ImportCatalogService } from '../../core/services/import-catalog.service';
import { ApiError, ImportResult, ImportCatalogEntrySummary, ManifestFileCategory, MediaDiscoveryRun } from '../../shared/models';
import { deriveSlug } from '../../shared/utils/slug';
import { formatDuration } from '../../shared/utils/format-duration';
import { formatBytes } from '../../shared/utils/format';
import { ASPECT_OPTIONS, ASPECT_PRESETS, deriveDimensionsMap } from '../../shared/utils/aspect-presets';

/** A ready catalog entry (DEV-403) plus this row's local import-in-progress state. */
interface CatalogEntryRow extends ImportCatalogEntrySummary {
	importing: boolean;
	error: string | null;
	runId: string | null;
	projectId: string | null;
	run: MediaDiscoveryRun | null;
	/** True while a "Retry" click on run.failedTasks is in flight — disables the button so a double-click can't queue two retries. */
	retryingFailedCopies?: boolean;
}

/** One row of the import pipeline's 5-step checklist. */
interface DisplayStep {
	key: string;
	label: string;
	status: 'pending' | 'active' | 'done' | 'error';
	trailing?: string;
	trailingDanger?: boolean;
}

type BatchRowStatus = 'pending' | 'importing' | 'done' | 'error';

interface BatchRow {
	url: string;
	status: BatchRowStatus;
	scenesImported?: number;
	imagesImported?: number;
	error?: string;
}

@Component({
	selector: 'app-import',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, InputText, InputNumber, Select, Textarea, ButtonDirective, RouterLink],
	templateUrl: './import.html',
	styleUrl: './import.scss',
})
export class ImportComponent implements OnInit {
	private service = inject(ProjectsService);
	private catalogService = inject(ImportCatalogService);
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private messages = inject(MessageService);
	private destroyRef = inject(DestroyRef);
	private auth = inject(AuthService);

	/** The S3 import catalog is admin-only — same gate as the backend's adminMiddleware. */
	protected isAdmin = computed(() => this.auth.user()?.isAdmin ?? false);

	protected mode = signal<'import' | 'batch' | 'create' | 'catalog'>('import');
	protected companySlug = '';
	protected campaignSlug = '';

	private nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');

	constructor() {
		// DEV-54: put the caret in the project-name field the moment create mode
		// opens, so "type the name first" needs no explanation. The create form
		// lives behind `@if (mode() === 'create')`, so the input does not exist
		// yet when the mode signal is written — the viewChild signal resolving is
		// the cue. This is a zoneless app, so no setTimeout: the effect re-runs on
		// its own once the query result changes. Covers both entry points (the
		// "Create New" pill and a direct /create URL handled in ngOnInit).
		effect(() => {
			if (this.mode() !== 'create') return;
			this.nameInput()?.nativeElement.focus();
		});
	}

	ngOnInit(): void {
		this.companySlug = this.route.snapshot.paramMap.get('companySlug') ?? '';
		this.campaignSlug = this.route.snapshot.paramMap.get('campaignSlug') ?? '';
		// Auto-detect mode from URL: last segment 'create' or 'import'
		const segments = this.route.snapshot.url;
		const lastSegment = segments[segments.length - 1]?.path;
		if (lastSegment === 'create') this.mode.set('create');
	}

	protected setMode(mode: 'import' | 'batch' | 'create' | 'catalog'): void {
		if (mode === 'catalog' && !this.isAdmin()) return;
		this.mode.set(mode);
		if (mode === 'catalog' && this.catalogEntries().length === 0 && !this.catalogLoading()) {
			this.loadCatalogEntries();
		}
	}

	private navigateToEditor(projectId: string): void {
		this.router.navigate(['/', this.companySlug, 'campaigns', this.campaignSlug, projectId, 'editor']);
	}

	// ── Import mode ─────────────────────────────────────────────────────────
	protected projectJsonUrl = '';
	protected loading = signal(false);
	protected result = signal<ImportResult | null>(null);
	protected error = signal<string | null>(null);

	submit(): void {
		if (!this.projectJsonUrl.trim()) return;
		this.loading.set(true);
		this.result.set(null);
		this.error.set(null);

		this.service.importProject(this.projectJsonUrl.trim(), this.companySlug || undefined, this.campaignSlug || undefined).subscribe({
			next: (res) => {
				this.loading.set(false);
				this.result.set(res);
			},
			error: (err) => {
				this.loading.set(false);
				this.error.set(err?.error?.error ?? 'Import failed');
			},
		});
	}

	// ── Batch import mode ───────────────────────────────────────────────────
	protected batchText = signal('');
	protected batchRows = signal<BatchRow[]>([]);
	protected batchRunning = signal(false);
	private batchSub: Subscription | null = null;

	protected batchSummary = () => {
		const rows = this.batchRows();
		const done = rows.filter(r => r.status === 'done').length;
		const failed = rows.filter(r => r.status === 'error').length;
		return { total: rows.length, done, failed };
	};

	private parseBatchUrls(text: string): string[] {
		return text.split('\n')
			.map(line => line.trim())
			.filter(line => line && !line.startsWith('#'));
	}

	protected submitBatch(): void {
		const urls = this.parseBatchUrls(this.batchText());
		if (!urls.length) return;

		const rows: BatchRow[] = urls.map(url => ({ url, status: 'pending' }));
		this.batchRows.set(rows);
		this.batchRunning.set(true);

		const updateRow = (index: number, patch: Partial<BatchRow>): void => {
			this.batchRows.update(list => {
				const next = [...list];
				next[index] = { ...next[index], ...patch };
				return next;
			});
		};

		this.batchSub = from(urls.map((url, i) => ({ url, i })))
			.pipe(
				concatMap(({ url, i }) => {
					updateRow(i, { status: 'importing' });
					return this.service.importProject(url, this.companySlug || undefined, this.campaignSlug || undefined).pipe(
						map(res => ({ i, res, error: null as string | null })),
						catchError((err: ApiError) => {
							const error = err?.error?.error ?? err?.message ?? 'Import failed';
							return of({ i, res: null as ImportResult | null, error });
						}),
					);
				}),
			)
			.subscribe({
				next: ({ i, res, error }) => {
					if (error) {
						updateRow(i, { status: 'error', error });
					} else if (res) {
						updateRow(i, {
							status: 'done',
							scenesImported: res.scenesImported,
							imagesImported: res.imagesImported,
						});
					}
				},
				complete: () => {
					this.batchRunning.set(false);
					this.batchSub = null;
				},
			});
	}

	protected cancelBatch(): void {
		this.batchSub?.unsubscribe();
		this.batchSub = null;
		this.batchRunning.set(false);
	}

	// ── Create mode ─────────────────────────────────────────────────────────
	protected newName = '';
	protected newSlug = '';
	protected newFps = 25;
	protected newWidth = 1920;
	protected newHeight = 1080;
	protected creating = signal(false);
	protected createError = signal<string | null>(null);

	protected aspectOptions = ASPECT_OPTIONS;
	protected selectedAspect = signal<string>('16:9');

	protected onAspectChange(value: string): void {
		this.selectedAspect.set(value);
		const preset = ASPECT_PRESETS[value];
		if (preset) {
			this.newWidth = preset.width;
			this.newHeight = preset.height;
		}
	}

	protected onNameChange(name: string): void {
		this.newName = name;
		this.newSlug = deriveSlug(name);
	}

	createProject(): void {
		if (!this.newName.trim()) return;
		this.creating.set(true);
		this.createError.set(null);

		const aspect = this.selectedAspect();
		const preset = ASPECT_PRESETS[aspect];
		const dimensionsMap = preset ? preset.dims : deriveDimensionsMap(this.newWidth, this.newHeight);

		this.service.createProject({
			name: this.newName.trim(),
			slug: this.newSlug || undefined,
			fps: this.newFps,
			origCoordsWidth: this.newWidth,
			origCoordsHeight: this.newHeight,
			dimensionsMap,
			companySlug: this.companySlug || undefined,
			campaignSlug: this.campaignSlug || undefined,
		}).subscribe({
			next: (res) => {
				this.creating.set(false);
				this.navigateToEditor(res._id);
			},
			error: (err: ApiError) => {
				this.creating.set(false);
				this.createError.set(err?.error?.error ?? err?.message ?? 'Create failed');
			},
		});
	}

	goToProjects(): void {
		this.router.navigate(['/']);
	}

	cancel(): void {
		this.router.navigate(['/']);
	}

	// ── S3 import catalog mode (DEV-403) ────────────────────────────────────
	protected catalogEntries = signal<CatalogEntryRow[]>([]);
	protected catalogLoading = signal(false);
	protected catalogLoadError = signal<string | null>(null);
	protected formatBytes = formatBytes;

	/** Same distinction as the admin catalog page — see its identically-named method's doc comment. */
	protected importableFileCount(row: CatalogEntryRow): number {
		const counts = row.manifest?.counts;
		if (!counts) return 0;
		const skipCategories: ManifestFileCategory[] = ['project-json', 'scene-json', 'image-json', 'ignored', 'unrecognized'];
		return (Object.entries(counts) as [ManifestFileCategory, number][])
			.filter(([category]) => !skipCategories.includes(category))
			.reduce((sum, [, count]) => sum + count, 0);
	}

	protected loadCatalogEntries(): void {
		this.catalogLoading.set(true);
		this.catalogLoadError.set(null);
		this.catalogService.listEntries().subscribe({
			next: (entries) => {
				this.catalogLoading.set(false);
				this.catalogEntries.set(
					entries.filter(e => e.status === 'ready')
						.map(e => ({ ...e, importing: false, error: null, runId: null, projectId: null, run: null })),
				);
			},
			error: (err: ApiError) => {
				this.catalogLoading.set(false);
				this.catalogLoadError.set(err?.error?.error ?? err?.message ?? 'Could not load import catalog');
			},
		});
	}

	private updateCatalogRow(id: string, patch: Partial<CatalogEntryRow>): void {
		this.catalogEntries.update(list => list.map(e => (e._id === id ? { ...e, ...patch } : e)));
	}

	private catalogRunSubs = new Map<string, Subscription>();

	protected importCatalogEntry(row: CatalogEntryRow): void {
		if (row.importing || row.run?.status === 'running') return;
		this.updateCatalogRow(row._id, { importing: true, error: null, run: null, projectId: null });
		this.catalogService.importFromCatalog(row._id, this.companySlug, this.campaignSlug || undefined).subscribe({
			next: ({ runId, projectId }) => {
				this.updateCatalogRow(row._id, { importing: false, runId, projectId });
				this.pollCatalogRun(row._id, runId);
			},
			error: (err: ApiError) => {
				this.updateCatalogRow(row._id, { importing: false, error: err?.error?.error ?? err?.message ?? 'Import failed' });
			},
		});
	}

	private pollCatalogRun(rowId: string, runId: string): void {
		this.catalogRunSubs.get(rowId)?.unsubscribe();
		const sub = timer(0, 1000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.catalogService.getImportRun(rowId, runId).subscribe({
				next: (run) => {
					this.updateCatalogRow(rowId, { run });
					if (run.status !== 'running') {
						this.catalogRunSubs.get(rowId)?.unsubscribe();
						this.catalogRunSubs.delete(rowId);
					}
				},
				error: () => {
					this.catalogRunSubs.get(rowId)?.unsubscribe();
					this.catalogRunSubs.delete(rowId);
				},
			});
		});
		this.catalogRunSubs.set(rowId, sub);
	}

	/** Re-attempts every file in run.failedTasks. Meant to be clicked repeatedly: it re-enables (button visible, not disabled) each time the run leaves 'running' with failedTasks still non-empty, so an admin can keep retrying, even one file at a time, until everything lands. */
	protected retryFailedCatalogCopies(row: CatalogEntryRow): void {
		const run = row.run;
		if (!run || !run.failedTasks.length || row.retryingFailedCopies || run.status === 'running') return;

		this.updateCatalogRow(row._id, { retryingFailedCopies: true });
		this.catalogService.retryFailedMediaCopies(row._id, run.runId).subscribe({
			next: () => {
				this.updateCatalogRow(row._id, { retryingFailedCopies: false });
				this.pollCatalogRun(row._id, run.runId);
			},
			error: (err: ApiError) => {
				this.updateCatalogRow(row._id, { retryingFailedCopies: false, error: err?.error?.error ?? err?.message ?? 'Retry failed' });
			},
		});
	}

	/** Same shape as mergedSteps() but for the catalog's MediaDiscoveryRun — no validate-schema step. */
	protected catalogSteps(row: CatalogEntryRow): DisplayStep[] {
		const run = row.run;
		if (!run) return [];
		return run.steps.filter(s => s.key !== 'validate-schema').map(s => ({
			key: s.key,
			label: ({ 'scan-repo': 'Scanning S3', 'write-catalog': 'Building media catalog', 'copy-files': 'Copying media', 'rewrite-refs': 'Finishing' } as Record<string, string>)[s.key] ?? s.key,
			status: s.status,
			trailing: s.durationMs != null ? formatDuration(s.durationMs) : undefined,
		}));
	}

	protected catalogEditorLink(row: CatalogEntryRow): (string | undefined)[] {
		return ['/', this.companySlug, 'campaigns', this.campaignSlug, row.projectId ?? undefined, 'editor'];
	}
}
